import type { UseQueryResult } from '@tanstack/react-query'
import {
  EffectivePrivacy,
  PrivacyState,
  type ReadableAccessMetadata,
  type WritableAccessMetadata,
  User,
  UserGroup,
  SimpleWorkspace,
} from '@opallabs/concepts'
import { Theme } from '@opallabs/gem'

export type SupportedResource =
  | 'content'
  | 'moment'
  | 'story'
  | 'view'
  | 'block'

export type SimplePrivacyState = PrivacyState.Private | PrivacyState.Public

export enum AccessSummaryState {
  SpecificCollaborators = 'collaborators',
  OnlyMe = 'only_me',
  Public = 'public',
  InheritedPublic = 'inherited_public',
}

export type PermUserGroup = UserGroup & { count: number }
export const isPermUserGroup = (
  candidate: PermUserGroup | unknown | undefined
): candidate is PermUserGroup =>
  typeof candidate === 'object' &&
  candidate !== null &&
  'type' in candidate &&
  candidate.type === 'user_group'

export type PermUserLike = PermUserGroup | User

export interface ResourceShareParameterMap
  extends Record<SupportedResource, Record<string, unknown>> {
  content: { momentId?: string; legacyContentId?: string }
  moment: { legacyMomentId?: string }
  story: { legacyStoryId?: string }
  view: { viewMode?: 'view' | 'edit' }
  block: ResourceShareParameterMap['view'] & {
    planId?: string
    pairedMomentId?: string | null
    /* Required for routing, until plan view route incorporates the parent block ID */
    isRootBlock?: boolean
  }
}

export type ResourceMeta<ResourceType extends SupportedResource> = {
  /** The name of the resource type to display in the modal. */
  name: string
  /** The Gem icon to use for the resource. */
  glyph: (theme: Theme, displayedType?: string) => string
  /**
   * A function to generate a complete URL to the resource.
   */
  getShareUrl: (
    params: {
      resourceId: string
      workspace: SimpleWorkspace
    } & ResourceShareParameterMap[ResourceType]
  ) => string
}

export type ResourceShareParams<T extends SupportedResource> = Parameters<
  ResourceMeta<T>['getShareUrl']
>[0]

export type ResourceDataParams = {
  effectivePrivacy?: EffectivePrivacy | null
  metadataQuery: UseQueryResult<ReadableAccessMetadata | undefined>
  ownerId?: string
  /** Overrides the displayed type name for the resource (e.g. "Plan" for `view`.) */
  resourceDisplayType?: string
  resourceId: string
  resourceTitle: string
  resourceType: SupportedResource
}

export type ResourceShareModalHandlers = {
  onChangeAccess: (access: WritableAccessMetadata) => Promise<void>
  onClose: () => void
}

export type ResourceShareModalProps = {
  className?: string
  isVisible: boolean
  readOnly: boolean
}
